<script setup lang="ts">
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { flattenObject } from '@/utils/InputProcessingUtilities';
import { kktsSplitToBase } from '@/utils/PortalConstants';
import { downloadResultsAsCsv } from '@/utils/file';
import KBtn from '@/components/btns/KBtn.vue';
import { useCloneJobDialog } from '@/composables/useCloneJobDialog';
import { useDialogStore } from '@/stores/useDialogStore';
import { useJobEditStore } from '@/stores/useJobEditStore';
import {
  fetchPostDespatchJobUndoAll,
  fetchPostJobEditCancel,
  fetchPostJobEditFutile,
  fetchSetJobEditAllocation,
} from '@/helpers/fetchers';
import { showErrorNotification } from '@/helpers/notifiers';
import * as routeNames from '@/constants/routeNames';

export interface Props {
  disabled?: boolean;
  stopsCount?: number;
  jobId?: number;
  jobDate?: string;
  driverCode?: number;
  canSchedule?: boolean;
  canScheduleLoading?: boolean;
  toggleCanSchedule?: (value: boolean) => Promise<void>;
}
const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  stopsCount: 2,
  jobId: undefined,
  jobDate: undefined,
  driverCode: undefined,
  canSchedule: false,
  canScheduleLoading: false,
  toggleCanSchedule: undefined,
});

const router = useRouter();
const dialogStore = useDialogStore();
const jobEditStore = useJobEditStore();
const cloneJobDialog = useCloneJobDialog();

const isLocked = computed(() => jobEditStore.lockState.isLocked);

function handleCancelJobClick(): void {
  if (!props.disabled) {
    dialogStore.open('jobEditCancelJob', {
      jobId: props.jobId,
      jobDate: props.jobDate,
      confirm: async (formData) => {
        const jobId = props.jobId;
        const jobDate = props.jobDate;
        if (!jobId || !jobDate) return;
        return jobEditStore.executeWithLock(async () => {
          return fetchPostJobEditCancel(
            { job_id: jobId, job_date: jobDate },
            { actor: formData.actor, reason: formData.reason },
          );
        });
      },
    });
  }
}

function handleFutileJobClick(): void {
  if (!props.disabled) {
    dialogStore.open('jobEditCloseAsFutile', {
      jobId: props.jobId,
      jobDate: props.jobDate,
      confirm: async (formData) => {
        const jobId = props.jobId;
        const jobDate = props.jobDate;
        if (!jobId || !jobDate) return;
        return jobEditStore.executeWithLock(async () => {
          return fetchPostJobEditFutile(
            { job_id: jobId, job_date: jobDate },
            { reason: formData.reason },
          );
        });
      },
    });
  }
}

function handleDuplicateJobClick(): void {
  if (props.disabled) return;
  if (props.jobId === undefined || props.jobDate === undefined) return;
  cloneJobDialog.open(props.jobId, props.jobDate);
}

function handleLockJobClick(): void {
  if (props.disabled) return;
  dialogStore.open('jobEditLockJob', {
    isLocked: isLocked.value,
    confirm: async () => {
      if (isLocked.value) {
        await jobEditStore.unlockJob();
      } else {
        await jobEditStore.lockJob();
      }
    },
  });
}

function handleSplitToBaseClick(): void {
  if (!props.disabled) {
    const job =
      props.jobId !== undefined &&
      props.jobDate !== undefined &&
      props.driverCode !== undefined
        ? {
            rockhopper_id: props.jobId,
            rockhopper_date: props.jobDate,
            driver_code: props.driverCode,
          }
        : undefined;

    dialogStore.open('jobEditSplitToBase', {
      stopsCount: props.stopsCount,
      job,
      confirm: async (formData) => {
        if (!job) return;
        const stopNumber = formData.stopNumber as 2 | 3;
        const baseNumber = kktsSplitToBase.indexOf(formData.warehouse) + 1;
        if (baseNumber < 1 || baseNumber > 7) return;
        const base = baseNumber as openapi.PostJobeditAllocation_Body['base'];

        return jobEditStore.executeWithLock(async () => {
          return fetchSetJobEditAllocation(
            { job_id: job.rockhopper_id, job_date: job.rockhopper_date },
            {
              action: 'tobase',
              driver: job.driver_code,
              stop_number: stopNumber,
              base,
            },
          );
        });
      },
    });
  }
}

function handleUndoAllChangesClick(): void {
  if (!props.disabled) {
    dialogStore.open('jobEditUndoAllChanges', {
      confirm: async () => {
        const jobId = props.jobId;
        const jobDate = props.jobDate;
        if (!jobId || !jobDate) return;
        await fetchPostDespatchJobUndoAll({
          job_id: jobId,
          job_date: jobDate,
        });
        await jobEditStore.loadJob(true);
      },
    });
  }
}

function handleViewJobDetailsClick(): void {
  if (!props.disabled && props.jobId !== undefined && props.jobDate) {
    void router.push({
      name: routeNames.DETAILED_QUERY_JOB,
      query: {
        job_id: props.jobId,
        job_date: props.jobDate,
      },
    });
  }
}

function handlePrintJobDetailsClick(): void {
  if (!props.disabled) {
    // delay so the v-menu close animation finishes before the print dialog
    // takes over (Vuetify auto-closes on click via close-on-content-click)
    setTimeout(() => {
      window.print();
    }, 500);
  }
}

function handleDownloadCsvFileClick(): void {
  if (!props.disabled) {
    const jobData = jobEditStore.jobData;
    if (!jobData) return;
    try {
      const flat = flattenObject(jobData);
      const stem = `${jobData.rockhopper_id}_${jobData.rockhopper_date}`;
      downloadResultsAsCsv([flat], stem);
    } catch {
      showErrorNotification({ title: 'Error downloading the CSV' });
    }
  }
}
</script>

<template>
  <div :class="$style.jobEditActionsVMenu" data-testid="jobEditActionsVMenu">
    <v-menu
      :close-on-content-click="true"
      location="bottom"
      transition="slide-y-transition"
    >
      <template #activator="{ props: menuProps }">
        <KBtn
          :disabled="props.disabled"
          size="xs"
          variant="text"
          icon="mdi-dots-vertical"
          v-bind="menuProps"
        ></KBtn>
      </template>

      <v-list density="compact" :class="$style.list">
        <v-list-item
          prepend-icon="mdi-close"
          title="Cancel Job"
          @click="handleCancelJobClick"
        />
        <v-list-item
          prepend-icon="mdi-cancel"
          title="Futile Job"
          @click="handleFutileJobClick"
        />

        <v-divider :class="$style.divider" />

        <v-list-item
          prepend-icon="mdi-content-copy"
          title="Duplicate Job"
          @click="handleDuplicateJobClick"
        />
        <v-list-item
          :prepend-icon="isLocked ? 'mdi-lock-open' : 'mdi-lock'"
          :title="isLocked ? 'Unlock Job' : 'Lock Job'"
          @click="handleLockJobClick"
        />
        <v-list-item
          prepend-icon="mdi-swap-horizontal"
          title="Split To Base"
          @click="handleSplitToBaseClick"
        />

        <v-divider :class="$style.divider" />

        <v-list-item
          prepend-icon="mdi-undo-variant"
          title="Undo All Changes"
          @click="handleUndoAllChangesClick"
        />

        <v-divider :class="$style.divider" />

        <v-list-item
          prepend-icon="mdi-printer"
          title="Print Job Details"
          @click="handlePrintJobDetailsClick"
        />
        <v-list-item
          prepend-icon="mdi-file-pdf-box"
          title="Export As PDF"
          disabled
        />
        <v-list-item
          prepend-icon="mdi-download"
          title="Download CSV File"
          @click="handleDownloadCsvFileClick"
        />

        <v-divider :class="$style.divider" />

        <v-list-item
          prepend-icon="mdi-open-in-new"
          title="View Job Details"
          @click="handleViewJobDetailsClick"
        />

        <v-divider :class="$style.divider" />

        <v-list-item :class="$style.canScheduleItem" @click.stop>
          <template #prepend>
            <v-switch
              :model-value="props.canSchedule"
              density="compact"
              hide-details
              color="primary"
              :disabled="props.disabled || props.canScheduleLoading"
              :loading="props.canScheduleLoading"
              :class="$style.canScheduleSwitch"
              @update:model-value="
                (v) => v !== null && props.toggleCanSchedule?.(v)
              "
            />
          </template>
          <v-list-item-title>Can Schedule</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<style module lang="scss">
@use '@/styles/colors';

.jobEditActionsVMenu {
  display: contents;

  :global {
    .v-list-item {
      color: colors.$color-text-primary;
    }

    .v-list-item__prepend > .v-icon {
      opacity: 1;
      font-size: 20px;
    }

    .v-list-item__content {
      margin-left: 8px;
    }

    .v-list-item__spacer {
      display: none;
    }
  }
}

.list {
  padding-right: 16px;
}

.divider {
  margin: 4px 0;
}

.canScheduleItem {
  :global {
    .v-list-item__prepend {
      align-items: center;
      margin-inline-end: 8px;
    }
  }
}

.canScheduleSwitch {
  margin-right: 20px;

  :global {
    .v-selection-control {
      min-height: unset;
    }
  }
}
</style>
